import { Suspense, lazy } from "react";
import ScrollToTop from "@/components/ScrollToTop";
import { useAiCrawlerTracking } from "@/hooks/useAiCrawlerTracking";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Outlet, Navigate } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import RedirectHandler from "@/components/RedirectHandler";
import SubdomainRedirect from "@/components/SubdomainRedirect";
import ErrorBoundary from "@/components/ErrorBoundary";
import usePerformanceMonitor from "./hooks/usePerformanceMonitor";
import { useCriticalResourcePreload } from "./components/performance/PreloadManager";
import { CartProvider } from "@/contexts/CartContext";
import { BookingProvider } from "@/contexts/BookingContext";
import { I18nProvider } from "@/contexts/I18nContext";
import { LocaleDetector } from "@/components/LocaleDetector";
import PublicChatbot from "@/components/chatbot/PublicChatbot";

import { FloatingCtaProvider } from "@/contexts/FloatingCtaContext";
import EnhancedOrganizationSchema from "@/components/SEO/EnhancedOrganizationSchema";

// Critical pages loaded immediately
import Home from "./pages/Home";

// Lazy load ALL other pages
const Experiencia = lazy(() => import("./pages/Experiencia"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));

const NotFound = lazy(() => import("./pages/NotFound"));
const Error404 = lazy(() => import("./pages/Error404"));
const Auth = lazy(() => import("./pages/Auth"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));

// Admin pages
const AdminRentalRooms = lazy(() => import("./pages/admin/AdminRentalRooms"));
const AdminRentalRoomEdit = lazy(() => import("./pages/admin/AdminRentalRoomEdit"));
const AdminWidgetAnalytics = lazy(() => import("./pages/admin/AdminWidgetAnalytics"));
const AdminHub = lazy(() => import("./pages/admin/AdminHub"));
const AdminAiCrawlers = lazy(() => import("./pages/admin/AdminAiCrawlers"));
const AdminSeo = lazy(() => import("./pages/admin/AdminSeo"));

// Admin guard
const AdminProtectedRoute = lazy(() => import("./components/AdminProtectedRoute"));
// Cliente Area está temporalmente deshabilitada (sólo admin tiene acceso al panel).
// Los componentes de /cliente se conservan en /pages/cliente para reactivar más tarde.

// Service Category Pages
const ServiciosIndex = lazy(() => import("./pages/servicios/ServiciosIndex"));
const GestoriaAdministracionIndex = lazy(() => import("./pages/servicios/gestoria-administracion/GestoriaAdministracionIndex"));
const GestoriaDesempleo = lazy(() => import("./pages/servicios/gestoria-administracion/GestoriaDesempleo"));
const GestoriaAdministracion = lazy(() => import("./pages/servicios/gestoria-administracion/GestoriaAdministracion"));
const GestoriaCVDocumentacion = lazy(() => import("./pages/servicios/gestoria-administracion/GestoriaCVDocumentacion"));
const GestoriaAcompanamientos = lazy(() => import("./pages/servicios/gestoria-administracion/GestoriaAcompanamientos"));
const GestoriaTraducciones = lazy(() => import("./pages/servicios/gestoria-administracion/GestoriaTraducciones"));
const GestoriaEmpresas = lazy(() => import("./pages/servicios/gestoria-administracion/GestoriaEmpresas"));
const GestoriaPaquetes = lazy(() => import("./pages/servicios/gestoria-administracion/GestoriaPaquetes"));
const AsesoriaLegalIndex = lazy(() => import("./pages/servicios/asesoria-legal/AsesoriaLegalIndex"));
const LegalExtranjeria = lazy(() => import("./pages/servicios/asesoria-legal/LegalExtranjeria"));
const LegalLaboral = lazy(() => import("./pages/servicios/asesoria-legal/LegalLaboral"));
const LegalFamilia = lazy(() => import("./pages/servicios/asesoria-legal/LegalFamilia"));
const LegalMercantil = lazy(() => import("./pages/servicios/asesoria-legal/LegalMercantil"));
const LegalInmobiliario = lazy(() => import("./pages/servicios/asesoria-legal/LegalInmobiliario"));
const CursosIdiomasIndex = lazy(() => import("./pages/servicios/cursos-idiomas/CursosIdiomasIndex"));
const InglesCursos = lazy(() => import("./pages/servicios/cursos-idiomas/InglesCursos"));
const AlemanCursos = lazy(() => import("./pages/servicios/cursos-idiomas/AlemanCursos"));
const PruebaNivelIngles = lazy(() => import("./pages/servicios/cursos-idiomas/PruebaNivelIngles"));
const ConsultoriaSegurosIndex = lazy(() => import("./pages/servicios/consultoria-seguros/ConsultoriaSegurosIndex"));
const SegurosSalud = lazy(() => import("./pages/servicios/consultoria-seguros/SegurosSalud"));
const SegurosHogar = lazy(() => import("./pages/servicios/consultoria-seguros/SegurosHogar"));
const SegurosVehiculos = lazy(() => import("./pages/servicios/consultoria-seguros/SegurosVehiculos"));
const SegurosVidaPension = lazy(() => import("./pages/servicios/consultoria-seguros/SegurosVidaPension"));
const SegurosEmpresariales = lazy(() => import("./pages/servicios/consultoria-seguros/SegurosEmpresariales"));
const SegurosAccidentesSalud = lazy(() => import("./pages/servicios/consultoria-seguros/SegurosAccidentesSalud"));
const SegurosViajeTransporte = lazy(() => import("./pages/servicios/consultoria-seguros/SegurosViajeTransporte"));
const SegurosComparador = lazy(() => import("./pages/servicios/consultoria-seguros/SegurosComparador"));
const SegurosSiniestros = lazy(() => import("./pages/servicios/consultoria-seguros/SegurosSiniestros"));
const SegurosBeneficiosSociales = lazy(() => import("./pages/servicios/consultoria-seguros/SegurosBeneficiosSociales"));
const SegurosFormacion = lazy(() => import("./pages/servicios/consultoria-seguros/SegurosFormacion"));
const SegurosAsignacionHijos = lazy(() => import("./pages/servicios/consultoria-seguros/SegurosAsignacionHijos"));
const SegurosReduccionPrima = lazy(() => import("./pages/servicios/consultoria-seguros/SegurosReduccionPrima"));
const SegurosBajaLaboral = lazy(() => import("./pages/servicios/consultoria-seguros/SegurosBajaLaboral"));
const SolucionesDigitalesAvanzadas = lazy(() => import("./pages/servicios/SolucionesDigitalesAvanzadas"));
const AlquileresVehiculos = lazy(() => import("./pages/servicios/AlquileresVehiculos"));
const AlquileresViviendas = lazy(() => import("./pages/servicios/AlquileresViviendas"));
const HousingZonesIndex = lazy(() => import("./pages/servicios/HousingZonesIndex"));
const HousingZoneDetail = lazy(() => import("./pages/servicios/HousingZoneDetail"));
const PropertyDetail = lazy(() => import("./pages/servicios/PropertyDetail"));
const Favoritos = lazy(() => import("./pages/servicios/Favoritos"));
const DestinationDetail = lazy(() => import("./pages/servicios/DestinationDetail"));
const PackRelocation = lazy(() => import("./pages/servicios/PackRelocation"));
const RelocationBasico = lazy(() => import("./pages/servicios/pack-relocation/RelocationBasico"));
const RelocationCompleto = lazy(() => import("./pages/servicios/pack-relocation/RelocationCompleto"));
const RelocationEmpresas = lazy(() => import("./pages/servicios/pack-relocation/RelocationEmpresas"));
const RelocationRetorno = lazy(() => import("./pages/servicios/pack-relocation/RelocationRetorno"));

// Zonas Pages
const ZonasIndex = lazy(() => import("./pages/zonas/ZonasIndex"));
const CountryZones = lazy(() => import("./pages/zonas/CountryZones"));
const CityZone = lazy(() => import("./pages/zonas/CityZone"));
const CityServicePage = lazy(() => import("./pages/zonas/CityServicePage"));
const LocalizedZonesRedirect = lazy(() => import("./components/LocalizedZonesRedirect"));

// Emprende Pages
const EmprendeIndex = lazy(() => import("./pages/emprende/EmprendeIndex"));
const TransmiteIdeas = lazy(() => import("./pages/emprende/TransmiteIdeas"));
const GanaDinero = lazy(() => import("./pages/emprende/GanaDinero"));
const SolucionesDigitales = lazy(() => import("./pages/emprende/SolucionesDigitales"));
const Colabora = lazy(() => import("./pages/emprende/Colabora"));

// About Pages
const SobreNosotros = lazy(() => import("./pages/SobreNosotros"));
const Opiniones = lazy(() => import("./pages/Opiniones"));

// Contact Pages
const ContactoIndex = lazy(() => import("./pages/contacto/ContactoIndex"));
const ContactoFormulario = lazy(() => import("./pages/contacto/ContactoFormulario"));
const OficinasOpfikon = lazy(() => import("./pages/contacto/OficinasOpfikon"));

// Legal Pages
const PoliticasIndex = lazy(() => import("./pages/politicas/PoliticasIndex"));
const AvisoLegal = lazy(() => import("./pages/politicas/AvisoLegal"));
const PoliticaPrivacidad = lazy(() => import("./pages/politicas/PoliticaPrivacidad"));
const PoliticaCookies = lazy(() => import("./pages/politicas/PoliticaCookies"));
const PoliticaGDPR = lazy(() => import("./pages/politicas/PoliticaGDPR"));

// Optimized QueryClient configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      retry: 1,
    },
  },
});

const PageLoader = () => null;

/**
 * All app routes - rendered under both "/" (default = ES) and "/:locale/"
 */
function AppRoutes() {
  return (
    <>
      <Route index element={<Home />} />
      <Route path="experiencia" element={<Experiencia />} />
      <Route path="auth" element={<Auth />} />
      <Route path="login" element={<Auth />} />
      <Route path="reset-password" element={<ResetPassword />} />
      
      {/* Cliente Area — temporalmente cerrado al público.
          Sólo el admin puede entrar al panel de habitaciones por ahora.
          Cualquier acceso a /cliente/* se redirige a la home. */}
      <Route path="cliente" element={<Navigate to="/" replace />} />
      <Route path="cliente/*" element={<Navigate to="/" replace />} />

      <Route path="sobre-nosotros" element={<SobreNosotros />} />
      <Route path="quienes-somos" element={<SobreNosotros />} />
      <Route path="quienes-somos/mision-vision" element={<SobreNosotros />} />
      <Route path="quienes-somos/valores" element={<SobreNosotros />} />
      <Route path="quienes-somos/carreras" element={<SobreNosotros />} />

      {/* Reseñas / Opiniones — captura "opiniones de punto hispano" */}
      <Route path="opiniones" element={<Opiniones />} />
      <Route path="resenas" element={<Opiniones />} />
      <Route path="reseñas" element={<Opiniones />} />
      <Route path="reviews" element={<Opiniones />} />
      <Route path="testimonios" element={<Opiniones />} />

      {/* Blog */}
      <Route path="blog" element={<Blog />} />
      <Route path="blog/:slug" element={<BlogPost />} />
      
      <Route path="contacto" element={<ContactoIndex />} />
      <Route path="contacto/formulario" element={<ContactoFormulario />} />
      <Route path="contacto/oficinas-opfikon" element={<OficinasOpfikon />} />

      {/* Services */}
      <Route path="servicios" element={<ServiciosIndex />} />
      
      {/* Gestoría y Administración */}
      <Route path="servicios/gestoria-administracion" element={<GestoriaAdministracionIndex />} />
      <Route path="servicios/gestoria-administracion/desempleo" element={<GestoriaDesempleo />} />
      <Route path="servicios/gestoria-administracion/administracion" element={<GestoriaAdministracion />} />
      <Route path="servicios/gestoria-administracion/cv" element={<GestoriaCVDocumentacion />} />
      <Route path="servicios/gestoria-administracion/acompanamientos" element={<GestoriaAcompanamientos />} />
      <Route path="servicios/gestoria-administracion/traducciones" element={<GestoriaTraducciones />} />
      <Route path="servicios/gestoria-administracion/paquetes" element={<GestoriaPaquetes />} />

      {/* Asesoría Legal */}
      <Route path="servicios/asesoria-legal" element={<AsesoriaLegalIndex />} />
      <Route path="servicios/asesoria-legal/extranjeria" element={<LegalExtranjeria />} />
      <Route path="servicios/asesoria-legal/laboral" element={<LegalLaboral />} />
      <Route path="servicios/asesoria-legal/familia" element={<LegalFamilia />} />
      <Route path="servicios/asesoria-legal/mercantil" element={<LegalMercantil />} />
      <Route path="servicios/asesoria-legal/inmobiliario" element={<LegalInmobiliario />} />

      {/* Cursos de Idiomas */}
      <Route path="servicios/cursos-idiomas" element={<CursosIdiomasIndex />} />
      <Route path="servicios/cursos-idiomas/ingles" element={<InglesCursos />} />
      <Route path="servicios/cursos-idiomas/aleman" element={<AlemanCursos />} />
      <Route path="prueba-nivel-ingles" element={<PruebaNivelIngles />} />

      {/* Seguros y Pensiones */}
      <Route path="servicios/consultoria-seguros" element={<ConsultoriaSegurosIndex />} />
      <Route path="servicios/consultoria-seguros/salud" element={<SegurosSalud />} />
      <Route path="servicios/consultoria-seguros/hogar" element={<SegurosHogar />} />
      <Route path="servicios/consultoria-seguros/vehiculos" element={<SegurosVehiculos />} />
      <Route path="servicios/consultoria-seguros/vida-pension" element={<SegurosVidaPension />} />
      <Route path="servicios/consultoria-seguros/empresariales" element={<SegurosEmpresariales />} />
      <Route path="servicios/consultoria-seguros/accidentes-salud" element={<SegurosAccidentesSalud />} />
      <Route path="servicios/consultoria-seguros/viaje-transporte" element={<SegurosViajeTransporte />} />
      <Route path="servicios/consultoria-seguros/comparador" element={<SegurosComparador />} />
      <Route path="servicios/consultoria-seguros/siniestros" element={<SegurosSiniestros />} />
      <Route path="servicios/consultoria-seguros/beneficios-sociales" element={<SegurosBeneficiosSociales />} />
      <Route path="servicios/consultoria-seguros/formacion" element={<SegurosFormacion />} />
      <Route path="servicios/consultoria-seguros/asignacion-hijos" element={<SegurosAsignacionHijos />} />
      <Route path="servicios/consultoria-seguros/reduccion-prima" element={<SegurosReduccionPrima />} />
      <Route path="servicios/consultoria-seguros/baja-laboral" element={<SegurosBajaLaboral />} />

      {/* Soluciones Digitales */}
      <Route path="servicios/soluciones-digitales-avanzadas" element={<SolucionesDigitalesAvanzadas />} />

      {/* Other Services */}
      <Route path="servicios/alquileres-vehiculos" element={<AlquileresVehiculos />} />
      <Route path="servicios/alquileres-viviendas" element={<AlquileresViviendas />} />
      <Route path="servicios/alquileres-viviendas/zonas" element={<HousingZonesIndex />} />
      <Route path="servicios/alquileres-viviendas/zonas/:zoneSlug" element={<HousingZoneDetail />} />
      <Route path="servicios/alquileres-viviendas/:id" element={<PropertyDetail />} />
      <Route path="servicios/alquileres-viviendas/habitacion/:id" element={<PropertyDetail />} />
      <Route path="servicios/favoritos" element={<Favoritos />} />
      <Route path="servicios/destino/:location" element={<DestinationDetail />} />
      <Route path="servicios/pack-relocation" element={<PackRelocation />} />
      <Route path="servicios/pack-relocation/basico" element={<RelocationBasico />} />
      <Route path="servicios/pack-relocation/completo" element={<RelocationCompleto />} />
      <Route path="servicios/pack-relocation/empresas" element={<RelocationEmpresas />} />
      <Route path="servicios/pack-relocation/retorno" element={<RelocationRetorno />} />

      {/* Zonas (canonical) */}
      <Route path="zonas" element={<ZonasIndex />} />
      <Route path="zonas/:pais" element={<CountryZones />} />
      <Route path="zonas/:pais/:ciudad" element={<CityZone />} />
      <Route path="zonas/:pais/:ciudad/:servicio" element={<CityServicePage />} />

      {/* Native-language zone slugs → redirect to canonical /zonas/... */}
      <Route path="zone" element={<LocalizedZonesRedirect />} />
      <Route path="zone/:pais" element={<LocalizedZonesRedirect />} />
      <Route path="zone/:pais/:ciudad" element={<LocalizedZonesRedirect />} />
      <Route path="zone/:pais/:ciudad/:servicio" element={<LocalizedZonesRedirect />} />
      <Route path="standorte" element={<LocalizedZonesRedirect />} />
      <Route path="standorte/:pais" element={<LocalizedZonesRedirect />} />
      <Route path="standorte/:pais/:ciudad" element={<LocalizedZonesRedirect />} />
      <Route path="standorte/:pais/:ciudad/:servicio" element={<LocalizedZonesRedirect />} />
      <Route path="zones" element={<LocalizedZonesRedirect />} />
      <Route path="zones/:pais" element={<LocalizedZonesRedirect />} />
      <Route path="zones/:pais/:ciudad" element={<LocalizedZonesRedirect />} />
      <Route path="zones/:pais/:ciudad/:servicio" element={<LocalizedZonesRedirect />} />

      {/* Emprende */}
      <Route path="emprende" element={<EmprendeIndex />} />
      <Route path="emprende/ideas" element={<TransmiteIdeas />} />
      <Route path="emprende/gana-dinero" element={<GanaDinero />} />
      <Route path="emprende/soluciones-digitales" element={<SolucionesDigitales />} />
      <Route path="emprende/colabora" element={<Colabora />} />

      {/* Legal Pages */}
      <Route path="politicas" element={<PoliticasIndex />} />
      <Route path="politicas/aviso-legal" element={<AvisoLegal />} />
      <Route path="politicas/privacidad" element={<PoliticaPrivacidad />} />
      <Route path="politicas/cookies" element={<PoliticaCookies />} />
      <Route path="politicas/gdpr" element={<PoliticaGDPR />} />

      {/* Admin */}
      <Route path="admin" element={<AdminProtectedRoute><AdminHub /></AdminProtectedRoute>} />
      <Route path="admin/habitaciones" element={<AdminProtectedRoute><AdminRentalRooms /></AdminProtectedRoute>} />
      <Route path="admin/habitaciones/:id" element={<AdminProtectedRoute><AdminRentalRoomEdit /></AdminProtectedRoute>} />
      <Route path="admin/widget-analytics" element={<AdminProtectedRoute><AdminWidgetAnalytics /></AdminProtectedRoute>} />
      <Route path="admin/ai-crawlers" element={<AdminProtectedRoute><AdminAiCrawlers /></AdminProtectedRoute>} />
      <Route path="admin/seo" element={<AdminProtectedRoute><AdminSeo /></AdminProtectedRoute>} />

      {/* Special Pages */}
      <Route path="404" element={<Error404 />} />
    </>
  );
}

function AiCrawlerTrackerInit() {
  useAiCrawlerTracking();
  return null;
}

function App() {
  usePerformanceMonitor();
  useCriticalResourcePreload();

  return (
    <QueryClientProvider client={queryClient}>
      <HelmetProvider>
        <TooltipProvider>
          <CartProvider>
          <BookingProvider>
            <Toaster />
              <BrowserRouter>
              <ScrollToTop />
              <AiCrawlerTrackerInit />
              <ErrorBoundary>
              <I18nProvider>
              <FloatingCtaProvider>
              <EnhancedOrganizationSchema />
              <SubdomainRedirect />
              <RedirectHandler />
              <LocaleDetector />
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  {/* Default routes (Spanish - no prefix) */}
                  <Route path="/">
                    {AppRoutes()}
                  </Route>

                  {/* Locale-prefixed routes: /de/*, /fr/*, /it/*, /pt/*, /en/* */}
                  {(['de', 'fr', 'it', 'pt', 'en'] as const).map((loc) => (
                    <Route key={loc} path={loc}>
                      {AppRoutes()}
                    </Route>
                  ))}

                  {/* 404 Page */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
              <PublicChatbot />
              </FloatingCtaProvider>
              </I18nProvider>
            </ErrorBoundary>
          </BrowserRouter>
          </BookingProvider>
          </CartProvider>
        </TooltipProvider>
      </HelmetProvider>
    </QueryClientProvider>
  );
}

export default App;
